import {
  Opcode,
  CellType,
  SenseTarget,
  NUM_PHEROMONE_CHANNELS,
  DIR_DX,
  DIR_DY,
  DIR_HERE,
  DIR_RANDOM,
  BC_STRIDE,
  type Ant,
  type SwarmMap,
} from './types';
import type { PRNG } from './prng';

// Pre-allocated buffers to avoid per-call array allocations
const matchBuf = new Int32Array(32);
const dirBuf = new Int32Array(32);

// Sparse pheromone tracking — set by simulation.ts once before the ant loop each tick
let _pheroActive: Uint8Array | null = null;
let _pheroList: Uint32Array | null = null;
let _pheroListLen = 0;

export function initPheroSparse(active: Uint8Array, list: Uint32Array, len: number): void {
  _pheroActive = active;
  _pheroList = list;
  _pheroListLen = len;
}

export function getPheroListLen(): number {
  return _pheroListLen;
}

function offsetToDir(dx: number, dy: number): number {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax >= ay) return dx > 0 ? 2 : 4; // E or W
  return dy < 0 ? 1 : 3; // N or S
}

export function tickAnt(
  ant: Ant,
  antIdx: number,
  bc: Int32Array,
  instrCount: number,
  map: SwarmMap,
  ants: Ant[],
  rng: PRNG,
  maxOps: number,
  antGrid: Uint8Array,
  senseRange: number = 1,
): boolean {
  if (instrCount === 0) return false;

  const w = map.width;
  const h = map.height;
  const cells = map.cells;
  const food = map.food;
  const pher = map.pheromones;
  const regs = ant.regs;

  let ops = 0;
  let pc = ant.pc;

  while (ops < maxOps) {
    if (pc >= instrCount) pc = 0;
    const base = pc * BC_STRIDE;
    const op = bc[base];
    const flags = bc[base + 1];
    const raw0 = bc[base + 2];
    const raw1 = bc[base + 3];
    const raw2 = bc[base + 4];
    ops++;

    switch (op) {
      case Opcode.SENSE: {
        const target = ((flags & 1) ? regs[raw0] : raw0) as SenseTarget;
        const destReg = raw1;
        let matchCount = 0;
        // Fast path for senseRange=1: check 4 cardinal neighbors directly
        // dir: N=1, E=2, S=3, W=4
        const ax = ant.x, ay = ant.y;
        for (let dir = 1; dir <= 4; dir++) {
          const nx = ax + DIR_DX[dir];
          const ny = ay + DIR_DY[dir];
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) {
            if (target === SenseTarget.WALL) matchBuf[matchCount++] = dir;
            continue;
          }
          const idx = ny * w + nx;
          let hit = false;
          switch (target) {
            case SenseTarget.FOOD: hit = food[idx] > 0; break;
            case SenseTarget.WALL: hit = cells[idx] === CellType.WALL; break;
            case SenseTarget.NEST: hit = cells[idx] === CellType.NEST; break;
            case SenseTarget.EMPTY: hit = cells[idx] !== CellType.WALL; break;
            case SenseTarget.ANT: hit = antGrid[idx] > 0; break;
          }
          if (hit) matchBuf[matchCount++] = dir;
        }
        if (matchCount === 0) {
          regs[destReg] = 0;
        } else if (matchCount === 1) {
          regs[destReg] = matchBuf[0];
        } else {
          regs[destReg] = matchBuf[rng.nextInt(matchCount)];
        }
        pc++;
        break;
      }

      case Opcode.SMELL: {
        const ch0 = (flags & 1) ? regs[raw0] : raw0;
        const ch = ch0 < 0 ? 0 : ch0 >= NUM_PHEROMONE_CHANNELS ? NUM_PHEROMONE_CHANNELS - 1 : ch0;
        const destReg = raw1;
        // Fast path for senseRange=1: read 4 cardinal neighbors directly
        const sx = ant.x, sy = ant.y;
        const sumN = (sy > 0) ? pher[((sy - 1) * w + sx) * NUM_PHEROMONE_CHANNELS + ch] : 0;
        const sumE = (sx < w - 1) ? pher[(sy * w + sx + 1) * NUM_PHEROMONE_CHANNELS + ch] : 0;
        const sumS = (sy < h - 1) ? pher[((sy + 1) * w + sx) * NUM_PHEROMONE_CHANNELS + ch] : 0;
        const sumW = (sx > 0) ? pher[(sy * w + sx - 1) * NUM_PHEROMONE_CHANNELS + ch] : 0;
        let bestSum = 0;
        let dirCount = 0;
        if (sumN > bestSum) { bestSum = sumN; dirCount = 0; dirBuf[dirCount++] = 1; }
        else if (sumN === bestSum && sumN > 0) { dirBuf[dirCount++] = 1; }
        if (sumE > bestSum) { bestSum = sumE; dirCount = 0; dirBuf[dirCount++] = 2; }
        else if (sumE === bestSum && sumE > 0) { dirBuf[dirCount++] = 2; }
        if (sumS > bestSum) { bestSum = sumS; dirCount = 0; dirBuf[dirCount++] = 3; }
        else if (sumS === bestSum && sumS > 0) { dirBuf[dirCount++] = 3; }
        if (sumW > bestSum) { bestSum = sumW; dirCount = 0; dirBuf[dirCount++] = 4; }
        else if (sumW === bestSum && sumW > 0) { dirBuf[dirCount++] = 4; }
        if (dirCount === 0) {
          regs[destReg] = 0;
        } else if (dirCount === 1) {
          regs[destReg] = dirBuf[0];
        } else {
          regs[destReg] = dirBuf[rng.nextInt(dirCount)];
        }
        pc++;
        break;
      }

      case Opcode.SNIFF: {
        const ch0 = (flags & 1) ? regs[raw0] : raw0;
        const ch = ch0 < 0 ? 0 : ch0 >= NUM_PHEROMONE_CHANNELS ? NUM_PHEROMONE_CHANNELS - 1 : ch0;
        const dir = (flags & 2) ? regs[raw1] : raw1;
        const destReg = raw2;
        if (dir < 0 || dir > DIR_HERE) {
          regs[destReg] = 0;
        } else {
          const nx = ant.x + DIR_DX[dir];
          const ny = ant.y + DIR_DY[dir];
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) {
            regs[destReg] = 0;
          } else {
            regs[destReg] = pher[(ny * w + nx) * NUM_PHEROMONE_CHANNELS + ch];
          }
        }
        pc++;
        break;
      }

      case Opcode.PROBE: {
        let dir = (flags & 1) ? regs[raw0] : raw0;
        if (dir === DIR_RANDOM) dir = rng.nextInt(4) + 1;
        const destReg = raw1;
        if (dir < 0 || dir > DIR_HERE) {
          regs[destReg] = 0;
        } else {
          const nx = ant.x + DIR_DX[dir];
          const ny = ant.y + DIR_DY[dir];
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) {
            regs[destReg] = CellType.WALL;
          } else {
            const idx = ny * w + nx;
            const ct = cells[idx];
            if (ct === CellType.WALL) regs[destReg] = CellType.WALL;
            else if (food[idx] > 0) regs[destReg] = CellType.FOOD;
            else if (ct === CellType.NEST) regs[destReg] = CellType.NEST;
            else regs[destReg] = CellType.EMPTY;
          }
        }
        pc++;
        break;
      }

      case Opcode.CARRYING:
        regs[raw0] = ant.carrying ? 1 : 0;
        pc++;
        break;

      case Opcode.SET:
        regs[raw0] = (flags & 2) ? regs[raw1] : raw1;
        pc++;
        break;

      case Opcode.ADD:
        regs[raw0] = (regs[raw0] + ((flags & 2) ? regs[raw1] : raw1)) | 0;
        pc++;
        break;

      case Opcode.SUB:
        regs[raw0] = (regs[raw0] - ((flags & 2) ? regs[raw1] : raw1)) | 0;
        pc++;
        break;

      case Opcode.MOD: {
        const divisor = (flags & 2) ? regs[raw1] : raw1;
        if (divisor !== 0) regs[raw0] = ((regs[raw0] % divisor) + divisor) % divisor;
        pc++;
        break;
      }

      case Opcode.MUL:
        regs[raw0] = (regs[raw0] * ((flags & 2) ? regs[raw1] : raw1)) | 0;
        pc++;
        break;

      case Opcode.DIV: {
        const divisor = (flags & 2) ? regs[raw1] : raw1;
        if (divisor !== 0) regs[raw0] = (regs[raw0] / divisor) | 0;
        pc++;
        break;
      }

      case Opcode.AND:
        regs[raw0] = regs[raw0] & ((flags & 2) ? regs[raw1] : raw1);
        pc++;
        break;

      case Opcode.OR:
        regs[raw0] = regs[raw0] | ((flags & 2) ? regs[raw1] : raw1);
        pc++;
        break;

      case Opcode.XOR:
        regs[raw0] = regs[raw0] ^ ((flags & 2) ? regs[raw1] : raw1);
        pc++;
        break;

      case Opcode.LSHIFT:
        regs[raw0] = (regs[raw0] << ((flags & 2) ? regs[raw1] : raw1)) | 0;
        pc++;
        break;

      case Opcode.RSHIFT:
        regs[raw0] = regs[raw0] >> ((flags & 2) ? regs[raw1] : raw1);
        pc++;
        break;

      case Opcode.RANDOM: {
        const max = (flags & 2) ? regs[raw1] : raw1;
        regs[raw0] = max > 0 ? rng.nextInt(max) : 0;
        pc++;
        break;
      }

      case Opcode.MARK: {
        const ch0 = (flags & 1) ? regs[raw0] : raw0;
        const ch = ch0 < 0 ? 0 : ch0 >= NUM_PHEROMONE_CHANNELS ? NUM_PHEROMONE_CHANNELS - 1 : ch0;
        const i0 = (flags & 2) ? regs[raw1] : raw1;
        const amount = i0 < 0 ? 0 : i0 > 255 ? 255 : i0;
        const cellIdx = ant.y * w + ant.x;
        const phIdx = cellIdx * NUM_PHEROMONE_CHANNELS + ch;
        const newVal = pher[phIdx] + amount;
        pher[phIdx] = newVal > 255 ? 255 : newVal;
        if (amount > 0 && _pheroActive !== null && !_pheroActive[cellIdx]) {
          _pheroActive[cellIdx] = 1;
          _pheroList![_pheroListLen++] = cellIdx;
        }
        pc++;
        break;
      }

      case Opcode.JMP:
        pc = (flags & 1) ? regs[raw0] : raw0;
        break;

      case Opcode.CALL:
        regs[raw0] = pc + 1;
        pc = raw1;
        break;

      case Opcode.JEQ: {
        const a = (flags & 1) ? regs[raw0] : raw0;
        const b = (flags & 2) ? regs[raw1] : raw1;
        pc = a === b ? raw2 : pc + 1;
        break;
      }

      case Opcode.JNE: {
        const a = (flags & 1) ? regs[raw0] : raw0;
        const b = (flags & 2) ? regs[raw1] : raw1;
        pc = a !== b ? raw2 : pc + 1;
        break;
      }

      case Opcode.JGT: {
        const a = (flags & 1) ? regs[raw0] : raw0;
        const b = (flags & 2) ? regs[raw1] : raw1;
        pc = a > b ? raw2 : pc + 1;
        break;
      }

      case Opcode.JLT: {
        const a = (flags & 1) ? regs[raw0] : raw0;
        const b = (flags & 2) ? regs[raw1] : raw1;
        pc = a < b ? raw2 : pc + 1;
        break;
      }

      case Opcode.MOVE: {
        let dir = (flags & 1) ? regs[raw0] : raw0;
        if (dir === DIR_RANDOM) dir = rng.nextInt(4) + 1;
        if (dir >= 1 && dir <= 4) {
          const nx = ant.x + DIR_DX[dir];
          const ny = ant.y + DIR_DY[dir];
          if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
            if (cells[ny * w + nx] !== CellType.WALL) {
              antGrid[ant.y * w + ant.x]--;
              antGrid[ny * w + nx]++;
              ant.x = nx;
              ant.y = ny;
            }
          }
        }
        pc++;
        ant.pc = pc;
        return false;
      }

      case Opcode.PICKUP: {
        if (!ant.carrying) {
          const idx = ant.y * w + ant.x;
          if (food[idx] > 0) {
            food[idx]--;
            ant.carrying = true;
          }
        }
        pc++;
        ant.pc = pc;
        return false;
      }

      case Opcode.DROP: {
        if (ant.carrying) {
          ant.carrying = false;
          const idx = ant.y * w + ant.x;
          if (cells[idx] === CellType.NEST) {
            pc++;
            ant.pc = pc;
            return true;
          }
          food[idx]++;
        }
        pc++;
        ant.pc = pc;
        return false;
      }

      case Opcode.ID:
        regs[raw0] = antIdx;
        pc++;
        break;

      case Opcode.NOP:
        pc++;
        break;

      case Opcode.TAG: {
        const val = (flags & 1) ? regs[raw0] : raw0;
        ant.tag = val < 0 ? 0 : val > 7 ? val & 7 : val;
        ops--;
        pc++;
        break;
      }
    }
  }

  ant.pc = pc;
  return false;
}
